"use client";

import React, { useState, useEffect, useRef } from "react";

export function PainAmplifier() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const stats = [
    { value: "40%", label: "Searches now on AI platforms" },
    { value: "64%", label: "Buyers act on AI suggestions" },
    { value: "86%", label: "B2B buyers use AI for research" },
  ];

  return (
    <section ref={sectionRef} className="py-12 sm:py-16 md:py-24 bg-surface">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div
          className={`text-center mb-8 sm:mb-12 md:mb-16 ${isVisible ? "animate-fade-in-up" : "opacity-0"}`}
        >
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-ink mb-3 sm:mb-4">
            40% of searches now happen on AI. Is your brand showing up?
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-ink-muted max-w-[700px] mx-auto">
            Type your brand name into ChatGPT right now. If you&apos;re not
            getting mentioned - your competitors are filling that gap, every
            single day.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 md:gap-6 mb-8 sm:mb-12">
          {stats.map((s, i) => (
            <div
              key={i}
              className={`p-5 sm:p-8 md:p-10 rounded-xl sm:rounded-2xl bg-surface-raised text-center transition-all duration-300 hover:shadow-xl hover:-translate-y-2 hover:scale-[1.02] ${isVisible ? `animate-count-up stagger-stat-${i + 1}` : "opacity-0"}`}
            >
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-accent mb-2 sm:mb-3 tracking-tight">
                {s.value}
              </h2>
              <p className="text-xs sm:text-sm text-ink-muted leading-relaxed">
                {s.label}
              </p>
            </div>
          ))}
        </div>

        {/* Challenge Box */}
        <div
          className={`max-w-[800px] mx-auto bg-accent/10 border border-accent/30 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 ${isVisible ? "animate-bounce-in animation-delay-500" : "opacity-0"}`}
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 bg-accent rounded-full flex items-center justify-center">
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5 text-on-accent"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <p className="text-accent font-bold text-base sm:text-lg mb-1 sm:mb-2">
                Try this right now
              </p>
              <p className="text-ink-muted text-sm sm:text-base">
                Open ChatGPT and type: &quot;What are the best [your product
                category] tools for B2B companies?&quot; - Is your brand in the
                answer? If not, that&apos;s the problem TopGEO.Ai solves.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const platformCards = [
  {
    name: "ChatGPT",
    description: "Most used AI globally. #1 platform for B2B vendor discovery.",
    stat: "180M+ monthly users",
  },
  {
    name: "Gemini + AI Overviews",
    description:
      "Replacing Google's blue links. Controls what appears in AI search answers.",
    stat: "Billions of Google searches impacted",
  },
  {
    name: "Perplexity",
    description:
      "Fastest-growing AI search for B2B professionals and tech buyers.",
    stat: "15M+ monthly active users",
  },
  {
    name: "Claude",
    description:
      "Enterprise-preferred AI. High adoption among decision-makers and analysts.",
    stat: "Fastest enterprise adoption rate",
  },
  {
    name: "Microsoft Copilot + Bing",
    description:
      "Embedded in Office 365 & Bing. Reaches enterprise buyers daily.",
    stat: "1B+ Windows devices, integrated",
  },
  {
    name: "Grok + Meta AI + DeepSeek",
    description:
      "Emerging platforms rapidly growing with younger B2B decision makers.",
    stat: "Combined 100M+ monthly users",
  },
];

export function PlatformCoverage() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Platform icons
  const platformIcons: Record<string, React.ReactNode> = {
    ChatGPT: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
        <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
      </svg>
    ),
    "Gemini + AI Overviews": (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C12 6.627 6.627 12 0 12c6.627 0 12 5.373 12 12 0-6.627 5.373-12 12-12-6.627 0-12-5.373-12-12z" />
      </svg>
    ),
    Perplexity: (
      <img
        src="/images/perplexity.png"
        alt="Perplexity"
        className="w-8 h-8 object-contain transition-all duration-300 drop-shadow-[0_0_0_white] group-hover:brightness-0 group-hover:invert"
      />
    ),
    Claude: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
        <path
          d="M12 6v12M6 12h12"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M8.5 8.5l7 7M15.5 8.5l-7 7"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M12 4v2M12 18v2M4 12h2M18 12h2M6.5 6.5l1.5 1.5M16 16l1.5 1.5M17.5 6.5l-1.5 1.5M8 16l-1.5 1.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    "Microsoft Copilot + Bing": (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
        <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
      </svg>
    ),
    "Grok + Meta AI + DeepSeek": (
      <svg
        className="w-6 h-6"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
        <path d="M2 17l10 5 10-5"></path>
        <path d="M2 12l10 5 10-5"></path>
      </svg>
    ),
  };

  return (
    <section
      id="platform-coverage"
      ref={sectionRef}
      className="py-20 md:py-28 bg-surface-raised"
    >
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div
          className={`text-center mb-12 md:mb-16 ${isVisible ? "animate-fade-in-up" : "opacity-0"}`}
        >
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-ink mb-4">
            One dashboard. Every AI platform.{" "}
            <span className="text-accent">Zero blind spots.</span>
          </h2>
          <p className="text-base md:text-lg text-ink-muted max-w-[700px] mx-auto">
            TopGEO.Ai monitors your brand&apos;s mentions, citations, and rankings
            across all the platforms your buyers are using right now.
          </p>
        </div>

        {/* Platform Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
          {platformCards.map((platform, index) => (
            <div
              key={index}
              className={`group bg-surface border border-hairline rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-2 hover:border-accent/50 hover:bg-surface-raised ${isVisible ? `animate-flip-in stagger-platform-${index + 1}` : "opacity-0"}`}
            >
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 bg-accent/10 rounded-lg sm:rounded-xl flex items-center justify-center text-accent transition-all duration-300 group-hover:bg-accent group-hover:text-on-accent group-hover:scale-110 [&>svg]:w-5 [&>svg]:h-5 sm:[&>svg]:w-6 sm:[&>svg]:h-6 [&>img]:w-6 [&>img]:h-6 sm:[&>img]:w-8 sm:[&>img]:h-8">
                  {platformIcons[platform.name] || (
                    <svg
                      className="w-6 h-6"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <rect
                        x="3"
                        y="3"
                        width="18"
                        height="18"
                        rx="2"
                        ry="2"
                      ></rect>
                    </svg>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-ink mb-1.5 sm:mb-2 group-hover:text-accent transition-colors">
                    {platform.name}
                  </h3>
                  <p className="text-xs sm:text-sm text-ink-muted mb-2 sm:mb-3">
                    {platform.description}
                  </p>
                  <p className="text-[10px] sm:text-xs text-accent font-semibold flex items-center gap-1">
                    <svg
                      className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {platform.stat}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const steps = [
  {
    number: "1",
    title: "Enter your domain",
    time: "30 seconds",
    desc: "Sign in with Google → add your website URL → choose Agency or In-House mode",
    icon: (
      <svg
        className="w-7 h-7"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="2" y1="12" x2="22" y2="12"></line>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
      </svg>
    ),
  },
  {
    number: "2",
    title: "Add your competitors",
    time: "1 minute",
    desc: "Select 3-10 brands. We benchmark your AI visibility against theirs side by side.",
    icon: (
      <svg
        className="w-7 h-7"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
      </svg>
    ),
  },
  {
    number: "3",
    title: "Configure your prompts",
    time: "2 minutes",
    desc: "Use our prompt library or write custom buyer-intent queries for your industry",
    icon: (
      <svg
        className="w-7 h-7"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="4 17 10 11 4 5"></polyline>
        <line x1="12" y1="19" x2="20" y2="19"></line>
      </svg>
    ),
  },
  {
    number: "4",
    title: "Get your GEO Visibility Score",
    time: "Instant + daily",
    desc: "Instant dashboard. Daily monitoring. Actionable recommendations - your AI presence, owned.",
    icon: (
      <svg
        className="w-7 h-7"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="12" y1="20" x2="12" y2="10"></line>
        <line x1="18" y1="20" x2="18" y2="4"></line>
        <line x1="6" y1="20" x2="6" y2="16"></line>
      </svg>
    ),
  },
];

export function HowItWorks() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="how-it-works"
      className="py-20 md:py-28 bg-surface overflow-hidden"
    >
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div
          className={`text-center mb-16 md:mb-20 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
        >
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-ink mb-4">
            Get your AI visibility report in{" "}
            <span className="text-accent">60 seconds</span>
          </h2>
          <p className="text-base md:text-lg text-ink-muted max-w-[550px] mx-auto">
            No dev needed. No technical setup. Built for founders, CMOs, and
            growth teams.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 md:gap-6">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`group bg-surface-raised rounded-2xl p-5 sm:p-6 md:p-8 transition-all duration-500 hover:shadow-xl hover:-translate-y-2 relative mt-4 sm:mt-5 ml-2 sm:ml-3 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}
              style={{ transitionDelay: `${index * 150 + 300}ms` }}
            >
              {/* Step Number Badge */}
              <div className="absolute -top-3 -left-3 w-8 h-8 sm:w-10 sm:h-10 bg-accent rounded-full flex items-center justify-center text-on-accent font-bold text-base sm:text-lg shadow-lg">
                {step.number}
              </div>

              {/* Icon */}
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-accent/10 flex items-center justify-center text-accent mb-3 sm:mb-4 transition-all duration-300 group-hover:bg-accent group-hover:text-on-accent [&>svg]:w-5 [&>svg]:h-5 sm:[&>svg]:w-7 sm:[&>svg]:h-7">
                {step.icon}
              </div>

              {/* Content */}
              <h3 className="font-bold text-base sm:text-lg text-ink mb-1">
                {step.title}
              </h3>
              <p className="text-[10px] sm:text-xs text-accent font-semibold mb-1.5 sm:mb-2">
                {step.time}
              </p>
              <p className="text-xs sm:text-sm text-ink-muted leading-relaxed">
                {step.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const partnerLogos = [
  { name: "Kissflow", src: "/images/partners/kissflow.svg", scale: 1 },
  { name: "LegalEase", src: "/images/partners/legalease.svg", scale: 1 },
  { name: "Zylen", src: "/images/partners/zylen.avif", scale: 1.3 },
  { name: "Superhost", src: "/images/partners/superhost.png", scale: 1.3 },
  { name: "HikeHealth", src: "/images/partners/hikehealth.png", scale: 2.1 },
  { name: "Logicoy", src: "/images/partners/logicoy.png", scale: 1.0 },
  {
    name: "Tamilpreneur",
    src: "/images/partners/tamilpreneur.png",
    scale: 1.45,
  },
];

// Arrow icons
const ChevronLeft = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

const ChevronRight = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

export function SocialProof() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-surface-raised">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div
          className={`text-center mb-12 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
        >
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-ink mb-4">
            Brands we work with
          </h2>
        </div>

        {/* Partner Logos Marquee */}
        <div className="marquee-wrapper mb-12">
          <button
            className="marquee-nav-btn marquee-nav-left"
            aria-label="Scroll left"
          >
            <ChevronLeft />
          </button>

          <div className="marquee-content">
            <div
              className="marquee-track animate-marquee"
              style={{ transform: "translateX(0)" }}
            >
              {partnerLogos.map((partner, index) => (
                <div
                  key={`first-${index}`}
                  className="partner-logo"
                  style={{ order: index }}
                >
                  <img
                    src={partner.src}
                    alt={partner.name}
                    className="partner-img"
                    style={
                      partner.scale !== 1
                        ? { transform: `scale(${partner.scale})` }
                        : undefined
                    }
                  />
                </div>
              ))}
              {partnerLogos.map((partner, index) => (
                <div
                  key={`second-${index}`}
                  className="partner-logo"
                  style={{ order: index + 6 }}
                >
                  <img
                    src={partner.src}
                    alt={partner.name}
                    className="partner-img"
                    style={
                      partner.scale !== 1
                        ? { transform: `scale(${partner.scale})` }
                        : undefined
                    }
                  />
                </div>
              ))}
            </div>
          </div>

          <button
            className="marquee-nav-btn marquee-nav-right"
            aria-label="Scroll right"
          >
            <ChevronRight />
          </button>
        </div>

      </div>
    </section>
  );
}

// Legacy Partners export for backwards compatibility
export function Partners({ page, bgStyle }: { page: string; bgStyle?: React.CSSProperties }) {
  return (
    <section
      className={`partners-section ${page === "home" ? "mt-10 mb-10 w-full" : ""}`}
      style={bgStyle}
    >
      <div
        className={`mx-auto px-4 md:px-8 lg:px-12 ${page === "home" ? "mt-10 mb-16" : "mb-10"}`}
      >
        {/* Was an <h1> ("Trusted by Leading Partners") — demoted to <h2> so each
            page keeps exactly one <h1> (the page hero). Heading relabeled because
            some logos (Zylen = parent company, Tamilpreneur = community brand) are
            not customers; "Brands we work with" is accurate without overclaiming.
            TODO: Replace with verified CUSTOMER logos (with written permission). */}
        <h2
          className={`text-ink font-bold text-center ${page === "home" ? "text-2xl md:text-3xl lg:text-4xl xl:text-5xl" : "text-xl md:text-2xl lg:text-3xl xl:text-4xl"}`}
        >
          Brands we work with
        </h2>
      </div>

      <div
        className={`${page === "home" ? "mt-10 mb-10 marquee-home-wrapper" : "marquee-wrapper"}`}
      >
        <button
          className="marquee-nav-btn marquee-nav-left"
          aria-label="Scroll left"
        >
          <ChevronLeft />
        </button>

        <div className="marquee-content">
          <div
            className="marquee-track animate-marquee"
            style={{ transform: "translateX(0)" }}
          >
            {partnerLogos.map((partner, index) => (
              <div
                key={`first-${index}`}
                className="partner-logo"
                style={{ order: index }}
              >
                <img
                  src={partner.src}
                  alt={partner.name}
                  className="partner-img"
                  style={
                    partner.scale !== 1
                      ? { transform: `scale(${partner.scale})` }
                      : undefined
                  }
                />
              </div>
            ))}
            {partnerLogos.map((partner, index) => (
              <div
                key={`second-${index}`}
                className="partner-logo"
                style={{ order: index + 6 }}
              >
                <img
                  src={partner.src}
                  alt={partner.name}
                  className="partner-img"
                  style={
                    partner.scale !== 1
                      ? { transform: `scale(${partner.scale})` }
                      : undefined
                  }
                />
              </div>
            ))}
          </div>
        </div>

        <button
          className="marquee-nav-btn marquee-nav-right"
          aria-label="Scroll right"
        >
          <ChevronRight />
        </button>
      </div>
    </section>
  );
}

const faqs = [
  {
    q: "I already do SEO. Why do I need GEO?",
    a: "SEO remains important for crawlability, authority, and demand capture. GEO adds a different measurement layer: whether your brand appears inside generated answers, which sources support it, and whether that answer creates a path to an enquiry.",
  },
  {
    q: "How quickly will I see results?",
    a: "There is no universal indexing or citation timeline. We establish a dated baseline, publish against the strongest evidence gaps, and report observed changes in answers, citations, page pickup, and enquiries without promising a fixed ranking date.",
  },
  {
    q: "Do I need a developer or technical team?",
    a: "TopGeo manages the publishing infrastructure and ongoing page operations. Your technical team may need to approve a DNS or proxy change and the agreed analytics or form setup, but no CMS migration or website rebuild is required.",
  },
  {
    q: "What's actually in the free report?",
    a: "The scope is based on your market and buyer questions. A report records the prompts and engines tested, the verbatim answer evidence, brand appearance and position, returned sources, relevant competitors, and the highest-priority supported actions.",
  },
];

// const PlusIcon = () => (
//   <svg
//     width="16"
//     height="16"
//     viewBox="0 0 14 14"
//     fill="none"
//     className="md:w-5 md:h-5"
//   >
//     <path fill="#1ca493" d="M14 8H8v6H6V8H0V6h6V0h2v6h6z" />
//   </svg>
// );

// const MinusIcon = () => (
//   <svg
//     width="16"
//     height="16"
//     viewBox="0 0 14 2"
//     fill="none"
//     className="md:w-5 md:h-5"
//   >
//     <path fill="#1ca493" d="M14 2H0V0h14z" />
//   </svg>
// );

export function FAQ() {
  const [openItems, setOpenItems] = useState<number[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const toggleItem = (index: number) => {
    setOpenItems((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  return (
    <section
      ref={sectionRef}
      id="faq"
      className="w-full bg-surface py-16 md:py-20 lg:py-24 overflow-hidden"
    >
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div
          className={`text-center mb-12 md:mb-16 ${isVisible ? "animate-fade-in-up" : "opacity-0"}`}
        >
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-ink mb-4">
            Common Questions
          </h2>
          <p className="text-base md:text-lg text-ink-muted">
            Everything you need to know about TopGEO.Ai
          </p>
        </div>

        {/* FAQ Items */}
        <div className="max-w-[900px] mx-auto flex flex-col gap-3 sm:gap-4 md:gap-6">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`bg-surface-raised rounded-xl sm:rounded-2xl overflow-hidden shadow-sm border border-hairline transition-all duration-300 hover:border-accent/30 hover:shadow-lg ${isVisible ? `animate-expand-in stagger-faq-${index + 1}` : "opacity-0"}`}
            >
              <button
                onClick={() => toggleItem(index)}
                className="w-full flex items-center justify-between p-4 sm:p-5 md:p-6 text-left hover:bg-surface transition-colors cursor-pointer group"
                aria-expanded={openItems.includes(index)}
                aria-controls={`faq-answer-${index}`}
              >
                <span className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-ink pr-3 sm:pr-6 leading-tight group-hover:text-accent transition-colors">
                  {faq.q}
                </span>
                <div
                  className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center transition-all duration-300 ${openItems.includes(index) ? "bg-accent rotate-180" : "bg-accent/10 group-hover:bg-accent/20"}`}
                >
                  <svg
                    className={`w-3 h-3 sm:w-4 sm:h-4 transition-colors ${openItems.includes(index) ? "text-on-accent" : "text-accent"}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline
                      points={
                        openItems.includes(index)
                          ? "18 15 12 9 6 15"
                          : "6 9 12 15 18 9"
                      }
                    ></polyline>
                  </svg>
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${openItems.includes(index) ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
              >
                {faq.a && (
                  <div
                    id={`faq-answer-${index}`}
                    className="px-4 sm:px-5 md:px-6 pb-4 sm:pb-5 md:pb-6 border-t border-hairline"
                  >
                    <div className="pt-3 sm:pt-4 md:pt-5">
                      <p className="text-xs sm:text-sm md:text-base lg:text-lg text-ink-muted leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FinalCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToForm = () => {
    const element = document.getElementById("hero-section");
    if (element) {
      const offsetTop = element.offsetTop;
      window.scrollTo({
        top: offsetTop + 30,
        behavior: "smooth",
      });
    }
  };

  return (
    <section
      ref={sectionRef}
      className="py-20 md:py-28 bg-surface-inverse relative overflow-hidden"
    >
      {/* Background decorations */}
      <div className="absolute -top-1/2 -right-1/5 w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(28,164,147,0.15)_0%,transparent_60%)] blur-[60px]"></div>
      <div className="absolute -bottom-1/3 -left-[10%] w-[400px] h-[400px] bg-[radial-gradient(circle,rgba(28,164,147,0.1)_0%,transparent_60%)] blur-[60px]"></div>

      <div className="max-w-[1200px] mx-auto px-4 md:px-8 lg:px-12 relative z-10">
        <div
          className={`text-center max-w-[700px] mx-auto px-2 ${isVisible ? "animate-fade-in-scale" : "opacity-0"}`}
        >
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
            Don&apos;t let your competitors own{" "}
            <span className="text-accent-ink">
              AI search
            </span>
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-white/70 mb-6 sm:mb-8">
            Every day you wait is a day your competitors get recommended and you
            don&apos;t.
            <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>
            Get your free AI Visibility Report in 60 seconds.
          </p>

          <button
            onClick={scrollToForm}
            className="inline-flex items-center justify-center w-full sm:w-auto px-5 sm:px-6 md:px-8 py-3 md:py-4 bg-accent hover:bg-accent/90 text-on-accent font-bold text-sm sm:text-base md:text-lg rounded-lg transition-all duration-300 hover:-translate-y-1 shadow-lg shadow-accent/30 cursor-pointer group"
          >
            <span className="text-center">Claim My Free AI Visibility Report Now</span>
            <svg
              className="w-4 h-4 md:w-5 md:h-5 ml-2 flex-shrink-0 transition-transform group-hover:translate-x-1 hidden sm:block"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </button>

          {/* Trust bullets */}
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 sm:gap-6 mt-6 sm:mt-8 text-xs sm:text-sm text-white/60">
          </div>
        </div>
      </div>
    </section>
  );
}

export function CTA() {
  return <FinalCTA />;
}

export function Stats() {
  return null;
}

export function Features() {
  return null;
}

export default Partners;
